// src/components/PixeladorFoto.jsx
// Editor simple para tapar caras/datos antes de subir (o volver a editar)
// una foto de "Nuestros Clientes": el admin dibuja rectángulos sobre la
// imagen y cada uno se pixela (mosaico, no rectángulo negro) al soltar,
// con historial para deshacer si tapó donde no era. Sin detección
// automática de caras todavía -- ver nota en el resumen del proyecto, es
// 100% manual por ahora.
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { pixelarZonas } from '../services/nuestrosClientesService';

const MAX_W = 620;

// source: { file: File } para una foto nueva, o { url: string } para
// volver a editar una que ya está subida (crossOrigin para poder exportar
// el canvas sin "mancharlo" -- el bucket de Storage sirve con CORS abierto).
export default function PixeladorFoto({ source, onConfirm, onCancel, saving }) {
  const canvasRef = useRef(null);
  const [zona, setZona] = useState(null); // rectángulo en curso
  const [drawing, setDrawing] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);
  const [historial, setHistorial] = useState([]); // dataURLs previos, para deshacer
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const img = new Image();
    if (source.url) img.crossOrigin = 'anonymous';
    img.onload = () => {
      const scale = Math.min(1, MAX_W / img.width);
      const canvas = canvasRef.current;
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      setReady(true);
    };
    const objectUrl = source.file ? URL.createObjectURL(source.file) : null;
    img.src = objectUrl || `${source.url}?_=${Date.now()}`;
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [source]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: Math.round((point.clientX - rect.left) * (canvasRef.current.width / rect.width)),
      y: Math.round((point.clientY - rect.top) * (canvasRef.current.height / rect.height)),
    };
  };

  const start = (e) => {
    if (!ready) return;
    e.preventDefault();
    const p = getPos(e);
    setDrawing(true);
    setZona({ x0: p.x, y0: p.y, x: p.x, y: p.y, w: 0, h: 0 });
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = getPos(e);
    setZona((z) => {
      if (!z) return z;
      const x = Math.min(z.x0, p.x), y = Math.min(z.y0, p.y);
      return { ...z, x, y, w: Math.abs(p.x - z.x0), h: Math.abs(p.y - z.y0) };
    });
  };

  const end = useCallback(() => {
    if (!drawing) return;
    setDrawing(false);
    setZona((z) => {
      if (z && z.w > 6 && z.h > 6) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        // Guarda el estado ANTES de pixelar (como datos de píxeles, no como
        // imagen codificada) para poder deshacer este paso al instante, sin
        // depender de decodificar nada -- eso era lo que a veces tardaba o
        // se trababa en teléfonos más lentos.
        setHistorial((h) => [...h, ctx.getImageData(0, 0, canvas.width, canvas.height)]);
        pixelarZonas(ctx, [{ x: z.x, y: z.y, w: z.w, h: z.h }]);
        setHasEdits(true);
      }
      return null;
    });
  }, [drawing]);

  const deshacer = () => {
    if (!historial.length) return;
    const prev = historial[historial.length - 1];
    canvasRef.current.getContext('2d').putImageData(prev, 0, 0);
    setHistorial((h) => h.slice(0, -1));
    setHasEdits(historial.length > 1);
  };

  const guardar = () => {
    canvasRef.current.toBlob((blob) => onConfirm(blob, hasEdits), 'image/jpeg', 0.88);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16, overflowY: 'auto' }}>
      <p style={{ color: 'var(--white)', fontSize: 13, fontWeight: 700, margin: '8px 0 4px', textAlign: 'center' }}>
        Dibuja un rectángulo sobre lo que quieras tapar (cara, nombre, dato) — se pixela al soltar
      </p>
      <p style={{ color: 'var(--muted)', fontSize: 11.5, marginBottom: 14, textAlign: 'center' }}>
        Si no hace falta tapar nada, guarda directo. ¿Te equivocaste de zona? Usa "Deshacer".
      </p>
      <div style={{ position: 'relative', touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ maxWidth: '100%', borderRadius: 10, border: '1px solid var(--navy-border)', cursor: 'crosshair', display: 'block' }}
        />
        {zona && (
          <div style={{
            position: 'absolute',
            left: `${(zona.x / canvasRef.current.width) * 100}%`,
            top: `${(zona.y / canvasRef.current.height) * 100}%`,
            width: `${(zona.w / canvasRef.current.width) * 100}%`,
            height: `${(zona.h / canvasRef.current.height) * 100}%`,
            border: '2px dashed var(--orange)', pointerEvents: 'none',
          }} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 18, width: '100%', maxWidth: 480 }}>
        <button onClick={onCancel} disabled={saving} style={{
          flex: 1, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)',
          borderRadius: 12, padding: '13px 0', fontSize: 13, fontWeight: 700,
        }}>Cancelar</button>
        <button onClick={deshacer} disabled={saving || !historial.length} style={{
          flex: 1, background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
          color: historial.length ? 'var(--gold, #FFB800)' : 'var(--muted)', opacity: historial.length ? 1 : 0.5,
          borderRadius: 12, padding: '13px 0', fontSize: 13, fontWeight: 700,
        }}>↶ Deshacer</button>
        <button onClick={guardar} disabled={saving || !ready} style={{
          flex: 2, background: 'var(--orange)', color: 'white', borderRadius: 12, padding: '13px 0',
          fontSize: 13.5, fontWeight: 800,
        }}>{saving ? 'Guardando...' : 'Guardar foto'}</button>
      </div>
    </div>
  );
}
